import React, { useState, useEffect } from "react";
import { Button, Tooltip, ConfigProvider, Badge, Drawer } from "antd";
import classes from "./DoubleSidebar.module.css";
import { Link, useNavigate } from "react-router-dom";
import { useAccount, useDoubleSidebar } from "@vojomo-core/shared";
import iconHome from "../../static/vojomo-v-icon.svg";
import {
  Bell,
  LogOut,
  Zap,
  Blocks,
  TrendingUp,
  FilePen,
  Sparkles,
  BookOpen,
  ClipboardList,
  type LucideIcon
} from "lucide-react";
import { useNotifications } from "../../hooks/use-notifications";
import { NotificationPanel } from "./NotificationPanel";

const SIDEBAR_ICON_MAP: Record<string, LucideIcon> = {
  icon_thunderbolt: Zap,
  icon_build: Blocks,
  icon_gain_momentum: TrendingUp,
  icon_doc_writer: FilePen,
  icon_ai: Sparkles,
  icon_learn: BookOpen,
  icon_coordinator: ClipboardList
};

export const DoubleSidebar: React.FC<{
  activeName: string;
  mainLinksData: {
    iconPath: string;
    title: string;
    directPath?: string;
  }[];
  linksData: any;
}> = ({ activeName, mainLinksData, linksData }) => {
  const navigate = useNavigate();
  const {
    isCollapsed,
    setIsCollapsed,
    isFirst,
    isLast,
    setIsFirst,
    setIsLast,
    expandedLabels,
    setExpandedLabels
  } = useDoubleSidebar();

  const { vojomoAccount, fullLogout } = useAccount();
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markRead,
    markAllRead
  } = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);

  const handleBellOpen = (open: boolean) => {
    setNotifOpen(open);
    if (open) fetchNotifications();
  };

  const mainKey = Object.keys(linksData).find((key) => {
    return linksData[key].some(
      (link: { title: string }) => link.title === activeName
    );
  });
  const userName = vojomoAccount.name.split(" ");

  const initials = userName
    .map((name) => name.charAt(0))
    .join("")
    .toUpperCase();

  const [active, setActive] = useState(
    mainKey === undefined ? "Vojomo" : mainKey
  );

  // State to track which tooltip is currently visible
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);

  const subLinks: {
    title: string;
    label: string;
    path?: string;
    tabs?: number;
    isLabel?: boolean;
    parentId?: string;
    onClick?: () => void | Promise<void>;
  }[] = linksData[active];

  const activeLabel = !activeName ? "Admin" : activeName;

  const mainLinksDataFiltered = mainLinksData.filter(
    (item) => item.title !== "Vojomo"
  );

  const mainLinks = mainLinksDataFiltered.map((link, index) => {
    const isSelected = link.title === active;
    const hoverBkgColor = isSelected ? "#ffffff" : "#A5ACC1";
    const isAboveSelected =
      mainLinksDataFiltered.findIndex((l) => l.title === active) === index + 1;
    const isBelowSelected =
      mainLinksDataFiltered.findIndex((l) => l.title === active) ===
        index - 1 && index - 1 >= 0;

    const handleMainLinkClick = (mainLink: string) => {
      // Hide tooltip when button is clicked
      setVisibleTooltip(null);

      // If the link has a directPath, navigate directly and collapse panel
      if (link.directPath) {
        navigate(link.directPath);
        setIsCollapsed(true);
        setActive(mainLink);
        return;
      }

      if (mainLink === active) {
        setIsCollapsed(!isCollapsed);
        return;
      } else {
        setActive(mainLink);
        setIsCollapsed(false);
      }
    };

    return (
      <Tooltip
        title={link.title}
        placement="right"
        arrow={true}
        key={link.title}
        open={
          link.title === active && !isCollapsed
            ? false
            : visibleTooltip === link.title
        }
        onOpenChange={(open) => {
          // Don't show tooltip if this is the active button
          // But allow tooltips to be shown when sidebar is collapsed
          if (link.title === active && !isCollapsed) {
            setVisibleTooltip(null);
            return;
          }

          if (open) {
            setVisibleTooltip(link.title);
          } else if (visibleTooltip === link.title) {
            setVisibleTooltip(null);
          }
        }}
      >
        <div style={{ backgroundColor: hoverBkgColor, borderRadius: "10px" }}>
          <Button
            type="text"
            onClick={() => handleMainLinkClick(link.title)}
            className={`${classes.mainLink} ${
              isAboveSelected ? classes.aboveSelected : ""
            } ${isBelowSelected ? classes.belowSelected : ""} ${
              isSelected ? classes.selected : ""
            }`}
            data-active={isSelected || undefined}
          >
            {(() => {
              const Icon = SIDEBAR_ICON_MAP[link.iconPath];
              return Icon ? (
                <Icon size={22} color={isSelected ? "black" : "#e8eaf0"} />
              ) : null;
            })()}
          </Button>
        </div>
      </Tooltip>
    );
  });

  useEffect(() => {
    const selectedIndex = mainLinksDataFiltered.findIndex(
      (l) => l.title === active
    );
    if (selectedIndex === 0 && !isFirst) {
      setIsFirst(true);
    } else if (selectedIndex !== 0 && isFirst) {
      setIsFirst(false);
    }
    if (selectedIndex === mainLinksDataFiltered.length - 1 && !isLast) {
      setIsLast(true);
    } else if (selectedIndex !== mainLinksDataFiltered.length - 1 && isLast) {
      setIsLast(false);
    }
  }, [active, mainLinksDataFiltered, isFirst, isLast, setIsFirst, setIsLast]);

  // Group links by their parent labels
  const groupedLinks: Record<string, typeof subLinks> = {};
  const topLevelLinks: typeof subLinks = [];

  // First pass: identify all labels
  subLinks.forEach((link) => {
    if (link?.isLabel === true) {
      groupedLinks[link.title] = [];
    }
  });

  // Second pass: group links by their explicit parent IDs
  subLinks.forEach((link) => {
    if (link?.isLabel === true) {
      // Labels go to top level
      topLevelLinks.push(link);
    } else if (link.parentId && groupedLinks[link.parentId]) {
      // Add to parent group if it has a parent
      groupedLinks[link.parentId].push(link);
    } else {
      // Otherwise add to top level
      topLevelLinks.push(link);
    }
  });

  const toggleLabel = (labelTitle: string) => {
    setExpandedLabels((prev) => ({
      ...prev,
      [labelTitle]: !prev[labelTitle]
    }));
  };

  const renderLinks = () => {
    return topLevelLinks.map((link) => {
      if (link?.isLabel === true) {
        // Get the expanded state for this label
        const isExpanded = expandedLabels[link.title] === true;
        const childLinks = groupedLinks[link.title] || [];

        return (
          <React.Fragment key={link.title}>
            <div
              className={classes.labelContainer}
              onClick={() => toggleLabel(link.title)}
            >
              <span className={classes.expandIcon}>
                {isExpanded ? "-" : "+"}
              </span>
              <span className={classes.label}>{link.label}</span>
            </div>
            {isExpanded &&
              childLinks.map((childLink) => {
                if (childLink.onClick) {
                  return (
                    <div
                      className={classes.link}
                      data-active={activeLabel === childLink.title || undefined}
                      key={childLink.title}
                      onClick={() => {
                        setIsCollapsed(true);
                        childLink.onClick?.();
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {childLink?.tabs ? (
                        <span className={classes.tabBadge}>
                          {"\u00A0".repeat(childLink.tabs * 3)}
                        </span>
                      ) : null}
                      {childLink.label}
                    </div>
                  );
                } else {
                  return (
                    <Link
                      className={classes.link}
                      data-active={activeLabel === childLink.title || undefined}
                      to={childLink.path}
                      key={childLink.title}
                      onClick={() => setIsCollapsed(true)}
                    >
                      {childLink?.tabs ? (
                        <span className={classes.tabBadge}>
                          {"\u00A0".repeat(childLink.tabs * 3)}
                        </span>
                      ) : null}
                      {childLink.label}
                    </Link>
                  );
                }
              })}
          </React.Fragment>
        );
      } else {
        if (link.onClick) {
          return (
            <div
              className={classes.link}
              data-active={activeLabel === link.title || undefined}
              key={link.title}
              onClick={() => {
                setIsCollapsed(true);
                link.onClick?.();
              }}
              style={{ cursor: "pointer" }}
            >
              {link?.tabs ? (
                <span className={classes.tabBadge}>
                  {"\u00A0".repeat(link.tabs * 5)}
                </span>
              ) : null}
              {link.label}
            </div>
          );
        } else {
          return (
            <Link
              className={classes.link}
              data-active={activeLabel === link.title || undefined}
              to={link.path}
              key={link.title}
              onClick={() => setIsCollapsed(true)}
            >
              {link?.tabs ? (
                <span className={classes.tabBadge}>
                  {"\u00A0".repeat(link.tabs * 5)}
                </span>
              ) : null}
              {link.label}
            </Link>
          );
        }
      }
    });
  };

  const divWrapperStyle = {
    width: "100%",
    backgroundColor: "#ffffff"
  };

  const divWrapperStyleUpperLowerBase = {
    width: "100%",
    height: "10px",
    backgroundColor: "#A5ACC1"
  };

  const divWrapperStyleLower = {
    ...divWrapperStyleUpperLowerBase,
    borderTopRightRadius: isLast ? "10px" : "0px"
  };

  const divWrapperStyleUpper = {
    ...divWrapperStyleUpperLowerBase,
    borderBottomRightRadius: isFirst ? "10px" : "0px"
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultColor: "white",
            defaultBorderColor: "white",
            defaultBg: "white",
            textTextHoverColor: "white",
            textHoverBg: "inherit",
            textTextActiveColor: "white",
            textTextColor: "white",
            defaultHoverBorderColor: "#A5ACC1"
          }
        }
      }}
    >
      <nav
        className={`${classes.navbar} ${isCollapsed ? classes.collapsed : ""}`}
      >
        <div className={classes.wrapper}>
          <div className={classes.aside}>
            <Link
              className={classes.logo}
              to="/"
              onClick={() => {
                setIsCollapsed(true);
                setActive("Vojomo");
              }}
            >
              <img
                src={iconHome}
                alt="Vojomo Logo"
                className="w-[38px] h-[38px]"
                style={{ transform: "rotate(27deg)" }}
              />
            </Link>

            <div style={divWrapperStyle}>
              <div style={divWrapperStyleUpper}></div>
              {mainLinks}
              <div style={divWrapperStyleLower}></div>
            </div>
            <div className={classes.footer}>
              <Drawer
                open={notifOpen}
                onClose={() => setNotifOpen(false)}
                placement="right"
                width={400}
                title="Notifications"
                styles={{ body: { padding: "12px 16px" } }}
              >
                <NotificationPanel
                  notifications={notifications}
                  unreadCount={unreadCount}
                  markRead={markRead}
                  markAllRead={markAllRead}
                  onClose={() => setNotifOpen(false)}
                />
              </Drawer>

              <div
                className={`${classes.footerLink} mb-4`}
                style={{ cursor: "pointer" }}
                onClick={() => handleBellOpen(true)}
              >
                <Tooltip title="Notifications" placement="right" arrow={true}>
                  <Badge count={unreadCount} size="small" offset={[4, -4]}>
                    <Bell size={22} color="#e8eaf0" />
                  </Badge>
                </Tooltip>
              </div>

              <Tooltip title="My Account" placement="right" arrow={true}>
                <Link
                  className={classes.avatar}
                  to="/account"
                  onClick={() => {
                    setIsCollapsed(true);
                    setActive("Vojomo");
                  }}
                >
                  {initials[0]}
                </Link>
              </Tooltip>

              <Link className={classes.footerLink} to="/" onClick={fullLogout}>
                <Tooltip title="Logout" placement="right" arrow={true}>
                  <LogOut size={22} color="#e8eaf0" />
                </Tooltip>
              </Link>
            </div>
          </div>
          <div
            className={`${classes.main} ${isCollapsed ? classes.hidden : ""}`}
          >
            <p className={classes.title}>{active}</p>
            <div className={classes.titleDivider}></div>
            {renderLinks()}
          </div>
        </div>
      </nav>
    </ConfigProvider>
  );
};
